import { PlusOutlined } from "@ant-design/icons";
import {
  Badge,
  Button,
  Flex,
  Select,
  Drawer,
  Dropdown,
  Modal,
  Tabs,
} from "antd";
import { Faders, CaretDown, BookOpenText, DotsThreeVertical } from "@phosphor-icons/react";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useNotification } from "../../../hooks/useNotification";
import { useBranchContext } from "../../../contexts/branch-context";
import { useResponsive } from "../../../hooks/useResponsive";
import { useTourContext } from "../../../contexts/tour-context/useTourContext";
import { useAdditionsTour } from "../../../contexts/additions-tour-context/useAdditionsTour";
import styled from "styled-components";
import { colors, spacing } from "../../../styles/theme";
import { isBranchCurrentlyOpen } from "../../../utils";

const FiltersContainer = styled.div<{ $visible: boolean }>`
  display: ${props => props.$visible ? 'flex' : 'none'};
  gap: ${spacing.md};
  padding: ${spacing.md};
  background: ${colors.white};
  border-top: 1px solid var(--color-divider);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  position: absolute;
  top: 60px;
  left: 0;
  right: 0;
  z-index: 1000;
  align-items: center;
`;

// ─── Mobile header styles ────────────────────────────────────────────────────

const MobileHeader = styled.div`
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-divider);
  padding: 12px 16px 0;
`;

const MobileTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const MobileTitle = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.2;
`;

const MobileButtonsRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 10px;

  /* First two buttons grow equally, icon button stays fixed */
  .mobile-action-btn {
    flex: 1;
    min-width: 0;
    justify-content: center;
  }
`;

const StatusBadgeButton = styled(Button)`
  border: none !important;
  background: transparent !important;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 12px;
  color: var(--color-text-secondary);
  text-align: left;

  &:active {
    opacity: 0.7;
  }
`;

const StatusDot = styled.span<{ $active: boolean }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.$active ? '#52c41a' : '#ff4d4f'};
  flex-shrink: 0;
`;

const MobileMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  min-height: 20px;
`;

const MetaSeparator = styled.span`
  color: var(--color-border);
  font-size: 12px;
`;

const BranchName = styled.span`
  font-size: 12px;
  color: var(--color-text-secondary);
`;

// Mobile tabs embedded in header — override Ant defaults to look integrated
const MobileTabsWrapper = styled.div`
  .ant-tabs-nav {
    margin: 0 !important;
  }
  .ant-tabs-nav::before {
    border-bottom: none !important;
  }
  .ant-tabs-tab {
    padding: 12px 16px !important;
    font-size: 13px !important;
    margin: 0 !important;
  }
  .ant-tabs-tab:first-child {
    margin-left: 0 !important;
  }
  .ant-tabs-ink-bar {
    background: ${colors.primary} !important;
    height: 3px !important;
    border-radius: 2px 2px 0 0 !important;
  }
  .ant-tabs-tab-active .ant-tabs-tab-btn {
    color: ${colors.primary} !important;
    font-weight: 600 !important;
  }
  .ant-tabs-tab-btn {
    color: var(--color-text-secondary) !important;
  }
`;

// ─── Desktop status line ──────────────────────────────────────────────────────

const SubInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StatusLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${colors.textSecondary};
`;

// ─── Mobile bottom drawer items ───────────────────────────────────────────────

const DrawerMenuItem = styled(Button)`
  border-radius: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  background: none;
  border: none;
  border-bottom: 1px solid var(--color-divider);
  cursor: pointer;
  font-size: 15px;
  color: var(--color-text);
  text-align: left;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--color-background-secondary);
  }
`;

const DrawerMenuItemPrimary = styled(DrawerMenuItem)`
  color: ${colors.primary};
  font-weight: 600;
`;

// ─── Misc ─────────────────────────────────────────────────────────────────────

const viewModeLabelKeys: Record<string, string> = {
  realizowane: "ordersHeader.viewModeRealizowane",
  kuchnia: "ordersHeader.viewModeKuchnia",
  dostawa: "ordersHeader.viewModeDostawa",
  wszystkie: "ordersHeader.viewModeWszystkie",
};

// ─── Types ────────────────────────────────────────────────────────────────────

export interface OrdersTabItem {
  key: string;
  label: string;
}

export const OrdersHeader: React.FC<{
  setHeight: any;
  setShowDone?: any;
  showDone?: any;
  setFilters?: any;
  filters?: any;
  onCreateOrder?: () => void;
  onCreateReservation?: () => void;
  viewMode?: "realizowane" | "kuchnia" | "dostawa" | "wszystkie";
  setViewMode?: (mode: "realizowane" | "kuchnia" | "dostawa" | "wszystkie") => void;
  onShowTablesView?: () => void;
  disableActions?: boolean;
  /** Ukrywa przyciski Realizowane/Stoliki/Filtry — używane gdy brak produktów w menu */
  hideOrderControls?: boolean;
  /** Mobile only — tabs rendered inside the header */
  mobileTabs?: OrdersTabItem[];
  mobileActiveTab?: string;
  onMobileTabChange?: (key: string) => void;
}> = ({
  setHeight,
  setShowDone,
  showDone,
  setFilters,
  filters,
  onCreateOrder,
  onCreateReservation,
  viewMode,
  setViewMode,
  onShowTablesView,
  disableActions = false,
  hideOrderControls = false,
  mobileTabs,
  mobileActiveTab,
  onMobileTabChange,
}) => {
    const { t } = useTranslation();
    const divRef = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();
    useNotification();
    const { currentBranch, availableBranches } = useBranchContext();
    const { isMobile } = useResponsive();
    const { menuNavRef, isTourOpen, tourStep, stepIndexes, advanceTourStep } = useTourContext();
    const { isTourOpen: additionsTourOpen, tourStep: additionsTourStep, advanceStep: advanceAdditionsStep } = useAdditionsTour();
    const [filtersVisible, setFiltersVisible] = useState(false);
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const [mobileFiltersModalOpen, setMobileFiltersModalOpen] = useState(false);

    const isOrdersPage = location.pathname === "/orders";
    const isReservationsPage = location.pathname === "/reservations";
    const isActive = isBranchCurrentlyOpen(currentBranch);
    const showBranchName = availableBranches.length > 1;

    const activeFiltersCount = [
      filters?.paymentStatus,
      filters?.paymentType,
      filters?.dateRange,
    ].filter(Boolean).length;

    useEffect(() => {
      if (divRef.current) {
        setHeight((divRef.current as any)?.offsetHeight);
      }
    });

    const viewModeMenuItems = [
      { key: "realizowane", label: t("ordersHeader.viewModeRealizowane") },
      { key: "kuchnia", label: t("ordersHeader.viewModeKuchnia") },
      { key: "dostawa", label: t("ordersHeader.viewModeDostawa") },
      { key: "wszystkie", label: t("ordersHeader.viewModeWszystkie") },
    ];

    // ── Mobile drawers ──────────────────────────────────────────────────────────

    const renderMobileOrdersDrawer = () => (
      <Drawer
        title={null}
        placement="bottom"
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        height="auto"
        styles={{ header: { display: 'none' }, body: { padding: 0 } }}
      >
        <DrawerMenuItem
          onClick={() => { setMobileDrawerOpen(false); setMobileFiltersModalOpen(true); }}
        >
          <Faders size={20} />
          {t("ordersHeader.filtry")} {activeFiltersCount > 0 && `(${activeFiltersCount})`}
        </DrawerMenuItem>
        {onShowTablesView && (
          <DrawerMenuItem
            onClick={() => { setMobileDrawerOpen(false); onShowTablesView(); }}
          >
            {t("ordersHeader.stoliki")}
          </DrawerMenuItem>
        )}
      </Drawer>
    );

    const renderMobileFiltersModal = () => (
      <Modal
        title={t("ordersHeader.filtry")}
        open={mobileFiltersModalOpen}
        onCancel={() => setMobileFiltersModalOpen(false)}
        footer={
          <Flex gap={8} justify="space-between">
            <Button
              onClick={() => {
                if (setFilters) setFilters({});
                setMobileFiltersModalOpen(false);
              }}
            >
              {t("ordersHeader.wyczyscFiltry")}
            </Button>
            <Button type="primary" onClick={() => setMobileFiltersModalOpen(false)}>
              {t("ordersHeader.zastosuj")}
            </Button>
          </Flex>
        }
      >
        <Flex vertical gap={16} style={{ paddingTop: 8 }}>
          <div>
            <label style={{ fontSize: '13px', color: colors.textSecondary, display: 'block', marginBottom: '6px' }}>
              {t("ordersHeader.statusPlatnosci")}
            </label>
            <Select
              placeholder={t("ordersHeader.wszystkie")}
              style={{ width: '100%' }}
              allowClear
              onChange={(value) => {
                if (setFilters) {
                  const newFilters = { ...filters };
                  if (value) { newFilters.paymentStatus = value; } else { delete newFilters.paymentStatus; }
                  setFilters(newFilters);
                }
              }}
              value={filters?.paymentStatus}
              options={[
                { label: t("ordersHeader.oplacone"), value: 'paid' },
                { label: t("ordersHeader.nieOplacone"), value: 'unpaid' },
              ]}
            />
          </div>
          <div>
            <label style={{ fontSize: '13px', color: colors.textSecondary, display: 'block', marginBottom: '6px' }}>
              {t("ordersHeader.typPlatnosci")}
            </label>
            <Select
              placeholder={t("ordersHeader.wszystkie")}
              style={{ width: '100%' }}
              allowClear
              onChange={(value) => {
                if (setFilters) {
                  const newFilters = { ...filters };
                  if (value) {
                    newFilters.paymentType = value;
                    newFilters.paymentsType = value;
                    delete newFilters.paymentStatus;
                  } else {
                    delete newFilters.paymentType;
                    delete newFilters.paymentsType;
                  }
                  setFilters(newFilters);
                }
              }}
              value={filters?.paymentType}
              options={[
                { label: t("ordersHeader.stripe"), value: 'STRIPE' },
                { label: t("ordersHeader.online"), value: 'PRZELEWY24' },
                { label: t("ordersHeader.offline"), value: 'OFFLINE' },
              ]}
            />
          </div>
          <div>
            <label style={{ fontSize: '13px', color: colors.textSecondary, display: 'block', marginBottom: '6px' }}>
              {t("ordersHeader.data")}
            </label>
            <Select
              placeholder={t("ordersHeader.wszystkie")}
              style={{ width: '100%' }}
              allowClear
              onChange={(value) => {
                if (setFilters) {
                  const newFilters = { ...filters };
                  if (value) {
                    const now = new Date();
                    let startDate: Date;
                    let endDate: Date;
                    const fmt = (date: Date) => {
                      const y = date.getFullYear();
                      const m = String(date.getMonth() + 1).padStart(2, '0');
                      const d = String(date.getDate()).padStart(2, '0');
                      return `${y}-${m}-${d}`;
                    };
                    switch (value) {
                      case 'today': startDate = endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()); break;
                      case 'yesterday': startDate = endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1); break;
                      case 'last3days': startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 2); endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()); break;
                      case 'last7days': startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6); endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()); break;
                      case 'last30days': startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 29); endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()); break;
                      case 'currentMonth': startDate = new Date(now.getFullYear(), now.getMonth(), 1); endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()); break;
                      case 'previousMonth': startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1); endDate = new Date(now.getFullYear(), now.getMonth(), 0); break;
                      default: startDate = endDate = new Date();
                    }
                    newFilters.dateRange = value;
                    newFilters.data = [fmt(startDate), fmt(endDate)];
                  } else {
                    delete newFilters.dateRange;
                    delete newFilters.data;
                  }
                  setFilters(newFilters);
                }
              }}
              value={filters?.dateRange}
              options={[
                { label: t("ordersHeader.dzis"), value: 'today' },
                { label: t("ordersHeader.wczoraj"), value: 'yesterday' },
                { label: t("ordersHeader.ostatnie3Dni"), value: 'last3days' },
                { label: t("ordersHeader.ostatnie7Dni"), value: 'last7days' },
                { label: t("ordersHeader.ostatnie30Dni"), value: 'last30days' },
                { label: t("ordersHeader.obecnyMiesiac"), value: 'currentMonth' },
                { label: t("ordersHeader.poprzedniMiesiac"), value: 'previousMonth' },
              ]}
            />
          </div>
        </Flex>
      </Modal>
    );

    // ── Status info modal ───────────────────────────────────────────────────────

    const renderStatusModal = () => {
      const title = isOrdersPage
        ? (isActive ? t("ordersHeader.zamowieniaAktywne") : t("ordersHeader.zamowieniaWylaczone"))
        : (isActive ? t("ordersHeader.rezerwacjeAktywne") : t("ordersHeader.rezerwacjeWylaczone"));

      return (
        <Modal
          title={title}
          open={statusModalOpen}
          onCancel={() => setStatusModalOpen(false)}
          footer={
            <Flex gap={8} justify="flex-end">
              <Button onClick={() => setStatusModalOpen(false)}>{t("ordersHeader.zamknij")}</Button>
              <Button
                type="primary"
                onClick={() => { setStatusModalOpen(false); navigate("/settings"); }}
              >
                {t("ordersHeader.przejdzDoUstawien")}
              </Button>
            </Flex>
          }
        >
          {isOrdersPage ? (
            isActive ? (
              <p style={{ margin: 0, lineHeight: 1.6 }}>
                {t("ordersHeader.zamawianieOnlineJest")} <strong>{t("ordersHeader.aktywne")}</strong>{t("ordersHeader.ordersActiveBody")}{" "}
                <a onClick={() => { setStatusModalOpen(false); navigate("/settings"); }} style={{ cursor: 'pointer' }}>
                  {t("ordersHeader.ustawieniach")}
                </a>.
              </p>
            ) : (
              <p style={{ margin: 0, lineHeight: 1.6 }}>
                {t("ordersHeader.zamawianieOnlineJest")} <strong>{t("ordersHeader.wylaczone")}</strong>{t("ordersHeader.ordersInactiveBody")}{" "}
                <a onClick={() => { setStatusModalOpen(false); navigate("/settings"); }} style={{ cursor: 'pointer' }}>
                  {t("ordersHeader.ustawien")}
                </a>.
              </p>
            )
          ) : (
            isActive ? (
              <p style={{ margin: 0, lineHeight: 1.6 }}>
                {t("ordersHeader.rezerwacjeOnlineSa")} <strong>{t("ordersHeader.aktywne")}</strong>{t("ordersHeader.reservationsActiveBody")}{" "}
                <a onClick={() => { setStatusModalOpen(false); navigate("/settings"); }} style={{ cursor: 'pointer' }}>
                  {t("ordersHeader.ustawieniach")}
                </a>.
              </p>
            ) : (
              <p style={{ margin: 0, lineHeight: 1.6 }}>
                {t("ordersHeader.rezerwacjeOnlineSa")} <strong>{t("ordersHeader.wylaczone")}</strong>{t("ordersHeader.reservationsInactiveBody")}{" "}
                <a onClick={() => { setStatusModalOpen(false); navigate("/settings"); }} style={{ cursor: 'pointer' }}>
                  {t("ordersHeader.ustawien")}
                </a>.
              </p>
            )
          )}
        </Modal>
      );
    };

    // ── Mobile layout ───────────────────────────────────────────────────────────

    if (isMobile) {
      return (
        <MobileHeader ref={divRef as any}>
          {/* Row 1: Title only */}
          <MobileTopRow>
            <MobileTitle>
              {isReservationsPage ? t("ordersHeader.rezerwacje") : t("ordersHeader.zamowienia")}
            </MobileTitle>
          </MobileTopRow>

          {/* Row 2: Status + branch name */}
          <MobileMetaRow>
            <StatusBadgeButton onClick={() => setStatusModalOpen(true)}>
              <StatusDot $active={!!isActive} />
              {isOrdersPage
                ? (isActive ? t("ordersHeader.sprzedazOnlineAktywna") : t("ordersHeader.sprzedazOnlineNieaktywna"))
                : (isActive ? t("ordersHeader.rezerwacjeOnlineAktywne") : t("ordersHeader.rezerwacjeOnlineNieaktywne"))}
            </StatusBadgeButton>
            {showBranchName && currentBranch?.name && (
              <>
                <MetaSeparator>|</MetaSeparator>
                <BranchName>{currentBranch.name}</BranchName>
              </>
            )}
          </MobileMetaRow>

          {/* Row 3: Main action buttons + 3-dot */}
          <MobileButtonsRow>
            {isOrdersPage && currentBranch?.sellType === "PRODUCTS_SELLER" && (
              <Button
                ref={menuNavRef}
                icon={<BookOpenText size={16} />}
                onClick={() => {
                  if (isTourOpen && tourStep === stepIndexes.MENU_NAV) advanceTourStep(stepIndexes.MENU_NAV);
                  // Additions tours – mobile MENU_NAV = step 3 in both tours
                  if (additionsTourOpen && additionsTourStep === 3) {
                    setTimeout(() => advanceAdditionsStep(3), 400);
                  }
                  navigate("/menu");
                }}
                className="mobile-action-btn"
                style={{ display: "flex", alignItems: "center" }}
              >
                {t("ordersHeader.menuProduktow")}
              </Button>
            )}
            {isReservationsPage && (
              <Button
                icon={<BookOpenText size={16} />}
                onClick={() => navigate("/services")}
                className="mobile-action-btn"
                style={{ display: "flex", alignItems: "center" }}
              >
                {t("ordersHeader.menuUslug")}
              </Button>
            )}
            {isOrdersPage && onCreateOrder && currentBranch?.sellType === "PRODUCTS_SELLER" && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={onCreateOrder}
                disabled={disableActions}
                className="mobile-action-btn"
              >
                {t("ordersHeader.noweZamowienie")}
              </Button>
            )}
            {isReservationsPage && onCreateReservation && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={onCreateReservation}
                disabled={disableActions}
                className="mobile-action-btn"
              >
                {t("ordersHeader.nowaRezerwacja")}
              </Button>
            )}
            {isOrdersPage && !hideOrderControls && (
              <Button
                icon={<DotsThreeVertical size={22} weight="bold" />}
                onClick={() => setMobileDrawerOpen(true)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
              />
            )}
          </MobileButtonsRow>

          {/* Row 4: Tabs — integrated into header */}
          {mobileTabs && mobileTabs.length > 0 && (
            <MobileTabsWrapper>
              <Tabs
                activeKey={mobileActiveTab}
                onChange={onMobileTabChange}
                items={mobileTabs.map(t => ({ key: t.key, label: t.label }))}
                tabBarStyle={{ marginBottom: 0 }}
              />
            </MobileTabsWrapper>
          )}

          {/* Modals & drawers */}
          {renderStatusModal()}
          {isOrdersPage && renderMobileOrdersDrawer()}
          {isOrdersPage && renderMobileFiltersModal()}
          {isReservationsPage && (
            <Drawer
              title={null}
              placement="bottom"
              open={mobileDrawerOpen}
              onClose={() => setMobileDrawerOpen(false)}
              height="auto"
              styles={{ header: { display: 'none' }, body: { padding: 0 } }}
            >
              {/* extend with future reservation actions */}
            </Drawer>
          )}
        </MobileHeader>
      );
    }

    // ── Desktop layout ──────────────────────────────────────────────────────────

    return (
      <div
        ref={divRef}
        style={{
          minHeight: "60px",
          paddingLeft: "4px",
          paddingRight: "4px",
          background: "var(--color-surface)",
          flex: "0 1 auto",
          position: "relative",
        }}
      >
        <Flex
          justify="space-between"
          align="center"
          style={{ minHeight: "60px", padding: "12px" }}
        >
          {/* LEFT: title + status + branch + dropdown + filters */}
          <Flex align="center" gap={12} style={{ paddingLeft: 12 }}>
            <div>
              <h2 style={{ margin: 0, lineHeight: 1.2 }}>
                {isReservationsPage ? t("ordersHeader.rezerwacje") : t("ordersHeader.zamowienia")}
              </h2>
              <SubInfo>
                <StatusLine>
                  <StatusDot $active={!!isActive} />
                  {isOrdersPage
                    ? (isActive ? t("ordersHeader.sprzedazOnlineAktywna") : t("ordersHeader.sprzedazOnlineNieaktywna"))
                    : (isActive ? t("ordersHeader.rezerwacjeOnlineAktywne") : t("ordersHeader.rezerwacjeOnlineNieaktywne"))}
                  {showBranchName && currentBranch?.name && (
                    <>
                      <MetaSeparator>|</MetaSeparator>
                      <span>{currentBranch.name}</span>
                    </>
                  )}
                </StatusLine>
              </SubInfo>
            </div>

            {setViewMode && currentBranch?.sellType === "PRODUCTS_SELLER" && isOrdersPage && !hideOrderControls && (
              <Flex gap={spacing.sm} align="center">
                <Dropdown
                  menu={{
                    items: viewModeMenuItems.map(item => ({
                      key: item.key,
                      label: item.label,
                      onClick: () => setViewMode(item.key as any),
                    })),
                    selectedKeys: viewMode ? [viewMode] : [],
                  }}
                  trigger={["click"]}
                >
                  <Button style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 500 }}>
                    {viewMode ? t(viewModeLabelKeys[viewMode]) : t("ordersHeader.viewModeRealizowane")}
                    <CaretDown size={14} />
                  </Button>
                </Dropdown>
                {onShowTablesView && (
                  <Button onClick={onShowTablesView}>
                    {t("ordersHeader.stoliki")}
                  </Button>
                )}
              </Flex>
            )}

            {isOrdersPage && !hideOrderControls && (
              <Badge count={activeFiltersCount} offset={[-5, 5]}>
                <Button
                  icon={<Faders size={20} />}
                  onClick={() => setFiltersVisible(!filtersVisible)}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                />
              </Badge>
            )}
          </Flex>

          {/* RIGHT: desktop action buttons */}
          <Flex gap={8} align="center">
            {isOrdersPage && currentBranch?.sellType === "PRODUCTS_SELLER" && (
              <Button
                ref={menuNavRef}
                icon={<BookOpenText size={16} />}
                onClick={() => {
                  if (isTourOpen && tourStep === stepIndexes.MENU_NAV) advanceTourStep(stepIndexes.MENU_NAV);
                  // Additions tours – desktop MENU_NAV = step 2 in both tours
                  if (additionsTourOpen && additionsTourStep === 2) {
                    setTimeout(() => advanceAdditionsStep(2), 400);
                  }
                  navigate("/menu");
                }}
                style={{ display: "flex", alignItems: "center" }}
              >
                {t("ordersHeader.menuProduktow")}
              </Button>
            )}
            {onCreateOrder && currentBranch?.sellType === "PRODUCTS_SELLER" && (
              <Button type="primary" icon={<PlusOutlined />} onClick={onCreateOrder} disabled={disableActions}>
                {t("ordersHeader.noweZamowienie")}
              </Button>
            )}
            {onCreateReservation && (
              <Button icon={<BookOpenText size={16} />} onClick={() => navigate("/services")} style={{ display: "flex", alignItems: "center" }}>
                {t("ordersHeader.menuUslug")}
              </Button>
            )}
            {onCreateReservation && (
              <Button type="primary" icon={<PlusOutlined />} onClick={onCreateReservation} disabled={disableActions}>
                {t("ordersHeader.nowaRezerwacja")}
              </Button>
            )}
          </Flex>
        </Flex>

        {/* Desktop filters panel */}
        {isOrdersPage && !hideOrderControls && (
          <FiltersContainer $visible={filtersVisible}>
            <Flex gap={12} style={{ width: '100%' }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '12px', color: colors.textSecondary, display: 'block', marginBottom: '4px' }}>
                  {t("ordersHeader.statusPlatnosciCap")}
                </label>
                <Select
                  placeholder={t("ordersHeader.wszystkie")}
                  style={{ width: '100%' }}
                  allowClear
                  onChange={(value) => {
                    if (setFilters) {
                      const newFilters = { ...filters };
                      if (value) { newFilters.paymentStatus = value; } else { delete newFilters.paymentStatus; }
                      setFilters(newFilters);
                    }
                  }}
                  value={filters?.paymentStatus}
                  options={[
                    { label: t("ordersHeader.oplacone"), value: 'paid' },
                    { label: t("ordersHeader.nieOplacone"), value: 'unpaid' },
                  ]}
                />
              </div>

              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '12px', color: colors.textSecondary, display: 'block', marginBottom: '4px' }}>
                  {t("ordersHeader.typPlatnosciCap")}
                </label>
                <Select
                  placeholder={t("ordersHeader.wszystkie")}
                  style={{ width: '100%' }}
                  allowClear
                  onChange={(value) => {
                    if (setFilters) {
                      const newFilters = { ...filters };
                      if (value) {
                        newFilters.paymentType = value;
                        newFilters.paymentsType = value;
                        delete newFilters.paymentStatus;
                      } else {
                        delete newFilters.paymentType;
                        delete newFilters.paymentsType;
                      }
                      setFilters(newFilters);
                    }
                  }}
                  value={filters?.paymentType}
                  options={[
                    { label: t("ordersHeader.stripe"), value: 'STRIPE' },
                    { label: t("ordersHeader.online"), value: 'PRZELEWY24' },
                    { label: t("ordersHeader.offline"), value: 'OFFLINE' },
                  ]}
                />
              </div>

              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '12px', color: colors.textSecondary, display: 'block', marginBottom: '4px' }}>
                  {t("ordersHeader.data")}
                </label>
                <Select
                  placeholder={t("ordersHeader.wszystkie")}
                  style={{ width: '100%' }}
                  allowClear
                  onChange={(value) => {
                    if (setFilters) {
                      const newFilters = { ...filters };
                      if (value) {
                        const now = new Date();
                        let startDate: Date;
                        let endDate: Date;
                        const formatLocalDate = (date: Date): string => {
                          const y = date.getFullYear();
                          const m = String(date.getMonth() + 1).padStart(2, '0');
                          const d = String(date.getDate()).padStart(2, '0');
                          return `${y}-${m}-${d}`;
                        };
                        switch (value) {
                          case 'today': startDate = endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()); break;
                          case 'yesterday': startDate = endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1); break;
                          case 'last3days': startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 2); endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()); break;
                          case 'last7days': startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6); endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()); break;
                          case 'last30days': startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 29); endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()); break;
                          case 'currentMonth': startDate = new Date(now.getFullYear(), now.getMonth(), 1); endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()); break;
                          case 'previousMonth': startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1); endDate = new Date(now.getFullYear(), now.getMonth(), 0); break;
                          default: startDate = endDate = new Date();
                        }
                        newFilters.dateRange = value;
                        newFilters.data = [formatLocalDate(startDate), formatLocalDate(endDate)];
                      } else {
                        delete newFilters.dateRange;
                        delete newFilters.data;
                      }
                      setFilters(newFilters);
                    }
                  }}
                  value={filters?.dateRange}
                  options={[
                    { label: t("ordersHeader.dzis"), value: 'today' },
                    { label: t("ordersHeader.wczoraj"), value: 'yesterday' },
                    { label: t("ordersHeader.ostatnie3Dni"), value: 'last3days' },
                    { label: t("ordersHeader.ostatnie7Dni"), value: 'last7days' },
                    { label: t("ordersHeader.ostatnie30Dni"), value: 'last30days' },
                    { label: t("ordersHeader.obecnyMiesiac"), value: 'currentMonth' },
                    { label: t("ordersHeader.poprzedniMiesiac"), value: 'previousMonth' },
                  ]}
                />
              </div>
            </Flex>
          </FiltersContainer>
        )}
      </div>
    );
  };
